/* eslint-disable */
"use client";

import { FloatingHeader } from "@/components/sections/FloatingHeader";
import { IntroBannerSection } from "@/components/sections/IntroBannerSection";
import { NewHeroSection } from "@/components/sections/NewHeroSection";
import { PaginatedReportListSection } from "@/components/sections/PaginatedReportListSection";
import React, { useEffect, useState, Suspense } from "react";
import { ThreeColumnClippingSection } from "@/components/sections/ThreeColumnClippingSection";
import { RankingReportSection } from "@/components/sections/RankingReportSection";
import { SignupBannerSection } from "@/components/sections/SignupBannerSection";
import { TwoRowFourColumnReportSection } from "@/components/sections/TwoRowFourColumnReportSection";
import { Footer } from "@/components/sections/Footer";
import PersonalizedGate from "@/components/common/PersonalizedGate";
import { CurationSections } from "@/components/sections/CurationSections";
import TokenActivationModal from "@/components/common/TokenActivationModal";
import PrivacyConsentModal from "@/components/common/PrivacyConsentModal";
import PasswordChangePromptModal from "@/components/common/PasswordChangePromptModal";
import JobSelectionModal from "@/components/common/JobSelectionModal";
import InterestSelectionModal from "@/components/common/InterestSelectionModal";
import ServiceExpiryModal from "@/components/common/ServiceExpiryModal";
import { useSearchParams } from "next/navigation";
import { getAuthStatus } from "@/lib/auth";
import { isServiceExpiringSoon, calculateDaysUntilExpiry } from "@/lib/service-utils";
import { authService } from "@/services/auth.service";
import { useRouter } from "next/navigation";

// import PrivacyConsentGate from "@/components/common/PrivacyConsentGate";

function MainPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [initialToken, setInitialToken] = useState<string | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [showServiceExpiryModal, setShowServiceExpiryModal] = useState(false);
  const [serviceExpiryDaysLeft, setServiceExpiryDaysLeft] = useState(0);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    // URL 파라미터에서 showTokenModal 확인
    const shouldShowModal = searchParams.get('showTokenModal') === 'true';
    const token = searchParams.get('token');
    
    if (shouldShowModal) {
      setShowTokenModal(true);
      setInitialToken(token);
      // activate 플로우 시작 표시
      sessionStorage.setItem('activateFlow', 'started');
      
      // URL에서 파라미터 제거 (새로고침 시 모달이 다시 뜨지 않도록)
      const url = new URL(window.location.href);
      url.searchParams.delete('showTokenModal');
      url.searchParams.delete('reason');
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams]);

  // 서비스 만료 임박 체크
  useEffect(() => {
    const checkServiceExpiry = async () => {
      try {
        const authData = await getAuthStatus();
        if (authData?.ok && authData?.user?.activation_token_expires_at) {
          if (isServiceExpiringSoon(authData.user.activation_token_expires_at)) {
            const daysLeft = calculateDaysUntilExpiry(authData.user.activation_token_expires_at);
            setServiceExpiryDaysLeft(daysLeft);
            setShowServiceExpiryModal(true);
          }
        }
      } catch (error) {
        console.error('서비스 만료 체크 중 오류:', error);
      }
    };

    // 다른 모달들이 모두 닫힌 후에 체크
    if (!showTokenModal && !showPrivacyModal && !showPasswordModal && !showJobModal && !showInterestModal) {
      checkServiceExpiry();
    }
  }, [showTokenModal, showPrivacyModal, showPasswordModal, showJobModal, showInterestModal]);

  const handleTokenModalClose = () => {
    setShowTokenModal(false);
    // activate 플로우 취소
    sessionStorage.removeItem('activateFlow');
    sessionStorage.removeItem('pendingUserCode');
    sessionStorage.removeItem('pendingUserIdx');
  };

  const handleTokenActivationSuccess = async (userInfo: any) => {
    // console.log('[Main] Token activation success, userInfo:', userInfo);
    
    // 토큰 확인 완료 후 사용자 정보를 sessionStorage에 저장하고 정보동의 모달 띄우기
    if (userInfo?.code) {
      // console.log('[Main] Saving userCode to sessionStorage:', userInfo.code);
      // console.log('[Main] Saving userIdx to sessionStorage:', userInfo.id);
      sessionStorage.setItem('pendingUserCode', userInfo.code);
      sessionStorage.setItem('pendingUserIdx', userInfo.id);
      sessionStorage.setItem('activateFlow', 'token_verified');
      
      // lastLogin이 null인지 확인 (백엔드에서 받은 정보)
      setIsFirstLogin(userInfo.lastLogin === null);
      setPendingUser(userInfo);
    } else {
      console.error('[Main] userInfo.code is missing!', userInfo);
    }
    setShowTokenModal(false);
    setShowPrivacyModal(true);
  };

  const handlePrivacyConsent = () => {
    // 정보동의 완료 후 비밀번호 변경 모달로 이동
    sessionStorage.setItem('activateFlow', 'privacy_completed');
    setShowPrivacyModal(false);
    setShowPasswordModal(true);
    
    // activateFlow에서는 항상 firstLogin으로 처리 (닫기 불가능한 모달)
    setIsFirstLogin(true);
    // console.log('[Main] Set isFirstLogin to: true (privacy consent)');
  };

  const handleTermsConsentComplete = (userInfo: any) => {
    // 약관 동의 완료 후 비밀번호 변경 모달로 이동
    // console.log('[Main] Terms consent completed, moving to password change modal');
    // console.log('[Main] User info:', userInfo);
    
    // userInfo를 pendingUser로 설정
    if (userInfo) {
      setPendingUser(userInfo);
    }
    
    sessionStorage.setItem('activateFlow', 'terms_completed');
    setShowPrivacyModal(false);
    setShowPasswordModal(true);
    
    // activateFlow에서는 항상 firstLogin으로 처리 (닫기 불가능한 모달)
    setIsFirstLogin(true);
    // console.log('[Main] Set isFirstLogin to: true (activateFlow)');
  };

  const handlePrivacyDisagree = () => {
    // 정보동의 거부 시 activate 플로우 취소
    setShowPrivacyModal(false);
    setPendingUser(null);
    sessionStorage.removeItem('activateFlow');
    sessionStorage.removeItem('pendingUserCode');
    sessionStorage.removeItem('pendingUserIdx');
  };

  const handlePasswordChangeComplete = async () => {
    // console.log('[Main] Password change complete, checking login status...');
    
    // 비밀번호 변경 완료 후 로그인 상태가 됨
    // 백엔드에서 JWT 토큰이 설정되어 로그인 처리됨
    
    // 비밀번호 모달 닫기
    setShowPasswordModal(false);
    
    // 로그인 상태 확인
    try {
      const result = await authService.getSession();
      // console.log('[Main] Login status after password change:', result);
      
      // To-Be 형식 처리
      if (result.code === "SUCCESS" && result.data) {
        const user = result.data;
        // console.log('[Main] User is logged in:', user);
        
        // 로그인 상태 변경을 알리는 커스텀 이벤트 발생
        window.dispatchEvent(new CustomEvent('authStatusChanged', { 
          detail: { loggedIn: true, user: user } 
        }));
        
        // activate 플로우 계속 진행 - 개인화 설정 (선택사항)
        setShowJobModal(true);
      } else {
        console.error('[Main] Login failed after password change');
        // 로그인 실패 시 페이지 새로고침
        window.location.reload();
      }
    } catch (error) {
      console.error('[Main] Error checking login status:', error);
    }
    
    // sessionStorage는 유지 (개인화 설정 완료 후 정리)
  };

  const handleJobComplete = async (job?: string) => {
    // 직업을 임시 저장 (관심주제에서 함께 저장할 예정)
    if (job) {
      // console.log('[Main] Job selected:', job);
      (window as any).__dw_pending_job__ = job;
    }
    
    // 직업 선택 완료 후 관심사 선택으로 이동
    setShowJobModal(false);
    setShowInterestModal(true);
  };

  const handleJobNext = (job: string | null) => {
    handleJobComplete(job || undefined);
  };

  const handleJobSkip = () => {
    // 직업 선택 스킵 - 관심사 선택으로 이동
    setShowJobModal(false);
    setShowInterestModal(true);
  };

  const handleJobModalClose = () => {
    // 모달 닫기 - 스킵과 동일하게 처리
    setShowJobModal(false);
    setShowInterestModal(true);
  };

  const handleInterestComplete = async (interests?: string[]) => {
    // 직무와 관심사를 함께 저장
    try {
      const job = (window as any).__dw_pending_job__ ?? null;
      // console.log('[Main] Saving personalization:', { job, interests });
      
      const result = await authService.savePersonalization({
        job: job,
        interests: interests ?? []
      });
      
      if (result.code === "SUCCESS") {
        // console.log('[Main] Personalization saved successfully');
        // 개인화 정보 저장 완료 이벤트 발생
        try { 
          window.dispatchEvent(new CustomEvent('dw:personalization_saved')); 
        } catch {}
        // 임시 저장된 직무 정보 정리
        try { delete (window as any).__dw_pending_job__; } catch {}
      } else {
        console.error('[Main] Failed to save personalization');
      }
    } catch (error) {
      console.error('[Main] Error saving personalization:', error);
    }
    
    // 모든 activate 플로우 완료
    setShowInterestModal(false);
    sessionStorage.removeItem('activateFlow');
    sessionStorage.removeItem('pendingUserCode');
    sessionStorage.removeItem('pendingUserIdx');
    // 이미 로그인된 상태이므로 새로고침으로 화면 갱신
    window.location.reload();
  };

  const handleInterestModalClose = () => {
    // 단순 모달 닫기 (스킵)
    setShowInterestModal(false);
    sessionStorage.removeItem('activateFlow');
    sessionStorage.removeItem('pendingUserCode');
    sessionStorage.removeItem('pendingUserIdx');
    // 이미 로그인된 상태이므로 새로고침으로 화면 갱신
    window.location.reload();
  };

  const handleInterestSkip = () => {
    // 관심사 선택 스킵 - activate 플로우 완료
    setShowInterestModal(false);
    sessionStorage.removeItem('activateFlow');
    sessionStorage.removeItem('pendingUserCode');
    sessionStorage.removeItem('pendingUserIdx');
    // 이미 로그인된 상태이므로 새로고침으로 화면 갱신
    window.location.reload();
  };

  const handleApproveUser = () => {
    // console.log('[Main] Approve user, userInfo:', userInfo);
    handleTokenModalClose();
    alert('이미 승인된 계정입니다.\n로그인 페이지로 이동합니다.');
    if (initialToken) {
      router.push(`/auth/login?token=${encodeURIComponent(initialToken)}`);
    } else {
      router.push(`/auth/login`);
    }
  };
  return (
    <main className="bg-[#ffffff] content-stretch flex flex-col items-center justify-start relative w-full">
      {/* Global consent gate moved to RootLayout */}
      <FloatingHeader hideSearch={true} />
      <IntroBannerSection />
      <NewHeroSection />
      <div className="bg-[#ffffff] box-border content-stretch flex flex-col gap-12 lg:gap-[100px] items-center justify-start overflow-clip px-4 lg:px-8 pt-6 pb-12 lg:py-[100px] relative shrink-0 w-full">
        <div className="w-full max-w-[1560px]">
          <PaginatedReportListSection blueTitle="8월의 DW'S PICK!" blackTitle="" columns={3} useCurationPick />
        </div>
        <PersonalizedGate>
          <div className="w-full max-w-[1560px]">
            <PaginatedReportListSection blueTitle="개인 맞춤형 " blackTitle="추천 콘텐츠" columns={3} usePersonalized />
          </div>
        </PersonalizedGate>
        <div className="w-full max-w-[1560px]">
          <ThreeColumnClippingSection 
            blueTitle="" 
            blackTitle="" 
            titleHtml="이번주에 <blue>알아두셔야 할 뉴스 소식</blue>이에요"
          />
        </div>
        <div className="w-full max-w-[1560px]">
          <TwoRowFourColumnReportSection 
            blueTitle="" 
            blackTitle="" 
            titleHtml="<blue>카테고리별</blue> 맞춤 콘텐츠 찾기"
          />
        </div>
        <div className="w-full max-w-[1560px]">
          <RankingReportSection
            blueTitle=""
            blackTitle=""
            titleHtml={"지금 <blue>가장 많이 읽는 글!</blue> 다들 보고 있어요"}
            showViewAll={false}
          />
        </div>
        <div className="hidden w-full max-w-[1560px]">
          <SignupBannerSection />
        </div>

        <CurationSections />
        
      </div>
      <Footer />
      
      {/* 토큰 활성화 모달 */}
      <TokenActivationModal
        isOpen={showTokenModal}
        onClose={handleTokenModalClose}
        onSuccess={handleTokenActivationSuccess}
        onTermsComplete={handleTermsConsentComplete}
        onApproveUser={handleApproveUser}
        initialToken={initialToken}
      />
      
      {/* 정보동의 모달 */}
      <PrivacyConsentModal
        open={showPrivacyModal}
        onClose={handlePrivacyDisagree}
        onAgree={handlePrivacyConsent}
        onDisagree={handlePrivacyDisagree}
        onTermsComplete={() => handleTermsConsentComplete(pendingUser)}
        userIdx={pendingUser?.id}
      />

      {/* 비밀번호 변경 모달 (activate 플로우) */}
      <PasswordChangePromptModal
        open={showPasswordModal}
        onClose={() => {
          if (!isFirstLogin) {
            setShowPasswordModal(false);
            handlePasswordChangeComplete();
          }
        }}
        onChange={handlePasswordChangeComplete}
        isFirstLogin={isFirstLogin}
        userIdx={pendingUser?.id}
        activationToken={initialToken}
      />

      {/* 직업 선택 모달 (activate 플로우) */}
      <JobSelectionModal
        open={showJobModal}
        onClose={handleJobModalClose}
        onSkip={handleJobSkip}
        onNext={handleJobNext}
      />

      {/* 관심사 선택 모달 (activate 플로우) */}
      <InterestSelectionModal
        open={showInterestModal}
        onClose={handleInterestModalClose}
        onBack={() => {
          setShowInterestModal(false);
          setShowJobModal(true);
        }}
        onSave={handleInterestComplete}
      />

      {/* 서비스 만료 임박 모달 */}
      <ServiceExpiryModal
        open={showServiceExpiryModal}
        onClose={() => setShowServiceExpiryModal(false)}
        daysLeft={serviceExpiryDaysLeft}
      />
    </main>
  );
}

export default function Page() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="size-10 animate-spin rounded-full border-4 border-[#dadbe0] border-t-[#43464d]" aria-label="로딩 중" />
      </div>
    }>
      <MainPageContent />
    </Suspense>
  );
}
